import { DialogContent, DialogTitle, FormControlLabel, ListItemText, Radio, Stack, Typography } from "@mui/material"
import { BoundRadioGroup, BoundTextField } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { DataInputDialog } from "lib/@dialogs/data-input-dialog"
import { useState } from "react"
import { GroupType } from "../../../../lib/enum/group-type.js"
import { Feature } from "lib/@components/feature"
import { FEATURE_FLAG_GLOBAL_UPDATES } from "authoring/lib/enum/authoring-feature-flags.mjs"

export function AddGroupDialog() {
    const { target } = useBoundContext()
    const names = target?.groups?.map(({ name }) => name)

    const [duplicate, setDuplicate] = useState(false)
    const [duplicateName, setDuplicateName] = useState("")
    return (
        <DataInputDialog
            initialValue={{ type: GroupType.ADD, name: "" }}
            okCaption="Add"
            okDisabled={(v) => isNameDuplicate(v?.name, names) || !v?.name?.trim()}
        >
            <DialogTitle mb={1} data-cy="add-group-dialog-title">
                Create New Group
            </DialogTitle>
            <DialogContent sx={{ pb: 0.5 }}>
                <Stack spacing={1} sx={{ mt: 1 }} data-cy="add-group-dialog">
                    <BoundTextField
                        field="name"
                        label="Group Name"
                        yup="string.required.trim"
                        error={duplicate}
                        helperText={
                            duplicate ? `There is already a group named: '${duplicateName}'. Please try again` : ""
                        }
                        onChanged={(v) => {
                            const duplicated = isNameDuplicate(v, names)
                            setDuplicate(duplicated)
                            setDuplicateName(v)
                        }}
                        placeholder="Enter a group name..."
                    />
                    <BoundRadioGroup field="type">
                        <Stack spacing={2}>
                            <Feature feature={FEATURE_FLAG_GLOBAL_UPDATES}>
                                <FormControlLabel
                                    value={GroupType.OVERWRITE}
                                    control={<Radio />}
                                    sx={{ fontWeight: "bold" }}
                                    label={
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                    Global Update
                                                </Typography>
                                            }
                                            secondary={"Select and group similar tasks, then apply changes in bulk."}
                                        />
                                    }
                                    data-cy="overwrite-tasks-checkbox"
                                />
                            </Feature>
                            <FormControlLabel
                                value={GroupType.ADD}
                                control={<Radio />}
                                label={
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                Add New Tasks
                                            </Typography>
                                        }
                                        secondary={
                                            "Create tasks, select references, and apply them across all schedules in your group."
                                        }
                                    />
                                }
                                data-cy="add-tasks-checkbox"
                            />
                            <FormControlLabel
                                value={GroupType.INTRODUCTION}
                                control={<Radio />}
                                label={
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                Add Introductions
                                            </Typography>
                                        }
                                        secondary={
                                            "Write an introduction and notes, then apply them across all schedules in your group."
                                        }
                                    />
                                }
                                data-cy="add-introductions-checkbox"
                            />
                            <FormControlLabel
                                value={GroupType.LEADING_SUMMARY}
                                control={<Radio />}
                                label={
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                Add Leading Summary & References
                                            </Typography>
                                        }
                                        secondary={
                                            "Write a leading summary, add references, or use both across all schedules in your group."
                                        }
                                    />
                                }
                                data-cy="add-leading-summaries-checkbox"
                            />
                        </Stack>
                    </BoundRadioGroup>
                </Stack>
            </DialogContent>
        </DataInputDialog>
    )
}

function isNameDuplicate(name, names) {
    return !!(name && names?.some((item) => item?.toLowerCase()?.trim() === name?.toLowerCase()?.trim()))
}
